import React, { useEffect, useRef, useState } from "react";
import Highcharts, { Dictionary, PlotLineOrBand } from "highcharts";
import highcharts3d from "highcharts/highcharts-3d";
import cylinder from "highcharts/modules/cylinder";
import { Box, useTheme } from "@mui/material";
import { ITagHistoryItem } from "../../services/tag-data-service/Models";
import { IDateRange } from "../../common/models/IDateRange";
import { ISetpointPlotlinesOptions } from "../../common/utilities/ChartHelpers";
import { IPoint } from "../../common/models/Point";
import TagHistoryUtils from "./TagHistoryUtils";
import { ITagViewState } from "../../services/state-data-service/Models";
import NoData from "../no-data/NoData";
import {isMobile, isTablet} from 'react-device-detect';
import { IListTextTagMapping_V2 } from "../../common/models/ILookupList";
import Graph, { GraphType } from "../graph/Graph";
import { GraphItemOptions, GraphOptions } from "../../services/trending-data-service/Models";
import GraphUtils from "../../common/utilities/GraphUtils";

highcharts3d(Highcharts);
cylinder(Highcharts);

export type TagValueChartProps = {
    tagDetailInfo: ITagViewState | undefined,
    tagHistoryItem: ITagHistoryItem | undefined,
    dateRange?: IDateRange,
    listTextTagMapping: IListTextTagMapping_V2 | undefined,
    showThreshold: boolean,
    noTitle?: boolean,
    noSubtitle?: boolean,
    noYAxisTitle?: boolean,
    minY?: number,
    maxY?: number
}

export default function TagValueChart({tagDetailInfo,
    tagHistoryItem,
    dateRange,
    listTextTagMapping,
    showThreshold,
    noTitle,
    noSubtitle,
    noYAxisTitle,
    minY,
    maxY
} : TagValueChartProps) {
    const [noDataMessage, setNoDataMessage] = useState<string>();
    const [options, setOptions] = useState<GraphOptions | undefined>();
    const [tipOpen, setTipOpen] = useState(false);
    const [tipText, setTipText] = useState<string>();
    const [tipPosition, setTipPosition] = useState<IPoint>();
    const [tipColor, setTipColor] = useState<string>();

    const theme = useTheme();
    const isPhone = isMobile && !isTablet;
    const graphRef = useRef<GraphType>(undefined);

    useEffect(() => {

        if (tagDetailInfo && tagHistoryItem) {
            setNoDataMessage(TagHistoryUtils.getChartNoDataMessage(tagDetailInfo.contextObject.objectName??"", tagHistoryItem.values.length, dateRange));
        }

    }, [tagDetailInfo,
        tagHistoryItem,
        dateRange,
        setNoDataMessage]);


    useEffect(() => {

        let timerID: number | undefined;

        const clearTimer = () => {
            setTipOpen(false);
            setTipPosition(undefined);
            setTipText(undefined);

            if (timerID) {
                clearTimeout(timerID);
                timerID = undefined;
            }
        };

        const startTimer = () => {

            clearTimer();

            timerID = window.setTimeout(() => {
                clearTimer();

            }, 3000);
        };


        const plotLinesMouseOverCallback = (item: ISetpointPlotlinesOptions, eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) => {

            const mouseEvent = eventArguments as MouseEvent;
            //console.log(`client: ${mouseEvent.clientX}, ${mouseEvent.clientY}  offset: ${mouseEvent.offsetX}, ${mouseEvent.offsetY}`);

            startTimer();


            setTipPosition({
                x: mouseEvent.offsetX,
                y: mouseEvent.offsetY
            });
            setTipColor(item.color);
            setTipText(item.tooltipText);
            setTipOpen(true);

        };

        const plotLinesMouseOutCallback = (item: ISetpointPlotlinesOptions, 
            eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) => {

            clearTimer();
        };

        if (tagDetailInfo && tagHistoryItem) {
            setTipOpen(false);

            const chartOptions = TagHistoryUtils.getChartOptions(
                theme,
                tagDetailInfo.contextObject.objectName??"",
                tagDetailInfo.tagUnits,
                tagHistoryItem,
                listTextTagMapping,
                '',
                showThreshold,
                isPhone,
                noTitle,
                noSubtitle,
                noYAxisTitle,
                minY,
                maxY);

                if (showThreshold) {
                    const items = chartOptions?.yAxis as Highcharts.YAxisOptions[];

                    items.forEach(o => {
                        o.plotLines?.forEach(v => {
                            v.events = {

                                mouseover: (eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) => {
                                    plotLinesMouseOverCallback(v as ISetpointPlotlinesOptions, eventArguments);

                                },
                                mouseout: (eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) => {
                                    plotLinesMouseOutCallback(v as ISetpointPlotlinesOptions, eventArguments);
                                }
                            }
                        });
                    });
                }

                const graphItemOptions: GraphItemOptions = {...chartOptions,
                    id: GraphUtils.getChartID(),
                    index: 0,
                };

                setOptions({ 
                    optionItems: [graphItemOptions] 
                });
               
        }

        return () => {

            if (timerID) {
                window.clearInterval(timerID);
            }
        }

    }, [
        listTextTagMapping,
        tagDetailInfo,
        isPhone,
        tagHistoryItem,
        dateRange,
        showThreshold,
        setOptions,
        setTipOpen,
        noTitle,
        noSubtitle,
        noYAxisTitle,
        theme,
        minY,
        maxY
    ]);


    // const chartCallback = (c: Highcharts.Chart) => {
    //     console.log(`chart callback: ${c.title}`);
    //     //setChart(c);
    // };

    // this effect should run on every render to ensure the chart fills the view
    // useEffect(() => {
    //     setTimeout(() => {
    //         if (Highcharts?.charts) {
    //             Highcharts.charts.forEach(chart => {
    //                 chart?.reflow();
    //         });
    //         }
    //     }, 500);
    // });
    // });


    return (

        <Box sx={{
            display: "flex",
            flexDirection: "column",
            flex: '1 1 auto',
            overflow: "hidden",
            overflowY: 'auto',
            position: 'relative'}}>


            {tagHistoryItem && tagHistoryItem.values.length <= 1 && noDataMessage &&
                <NoData text={noDataMessage} />
            }


            {(tagHistoryItem?.values.length ?? 0) > 1 && options !== undefined ?


            // <HighchartsReact
            //     ref={myRef}
            //     highcharts={Highcharts}
            //     options={options}
            //     callback={chartCallback}
            //     containerProps={{
            //         style: { height: "100%" }
            //     }}

            // /> 

            <Graph ref={graphRef} options={options} />


            : 
            null}


            {tipOpen && tipPosition && tipText && <Box sx={{
                position: 'absolute',
                top: `${tipPosition?.y}px`,
                left: `${tipPosition?.x}px`,
                color: `${tipColor}`,
                zIndex:100,
                border: '1px solid #e8e8e8',
                padding: theme.spacing(1),
                backgroundColor: 'white',
                boxShadow: '2px 2px silver',
                opacity: .8}}>
                    {tipText}
                </Box>}

        </Box>
    );
}
